/**
 * Harbour API → view models. Frontend must not invent why-here / plays.
 * Cards always have venueId. Pulse is counts. Search is capped. Drawer is one id.
 */

import dayjs from 'dayjs'
import {
  HARBOUR_BOARD_FORBIDDEN_QUERY_KEYS,
  HARBOUR_BOARD_PAYLOAD_MAX,
  HARBOUR_BOARD_SECTION,
  HARBOUR_BOARD_VISIBLE_COLUMNS,
  HARBOUR_COLUMN,
  HARBOUR_DEFAULT_COLUMN,
  HARBOUR_DEFAULT_HAS_PHONE,
  HARBOUR_DEFAULT_HIDE_PARKED,
  HARBOUR_DEFAULT_OWNER_FILTER,
  HARBOUR_DEFAULT_PLAN_FILTER,
  HARBOUR_DEFAULT_THRIVING,
  HARBOUR_DRAWER_LINKED_DEALS_LIMIT,
  HARBOUR_LAST_USED_FEATURE,
  HARBOUR_LAST_USED_FEATURES,
  HARBOUR_LAST_USED_STATUS,
  HARBOUR_MAX_TALKING_POINTS,
  HARBOUR_NEEDS_YOU_CAP,
  HARBOUR_PULSE_FORBIDDEN_KEYS,
  HARBOUR_SEARCH_LIMIT,
  formatHarbourNeedsYouCaption,
  formatHarbourNeedsYouCaptionDetail,
  shouldShowHarbourNeedsYouIceberg,
  formatHarbourSisterGroupNudge,
  formatHarbourSisterTalkingPoint,
  HARBOUR_SISTER_GROUP_GAP_LABEL,
  isHarbourAdoptionGap,
  getHarbourColumnLabel,
  getHarbourInboundSourceLabel,
  getHarbourIntentLabel,
  getHarbourLastUsedFeatureLabel,
  getHarbourLifecycleLabel,
  getHarbourOutcomeLabel,
  isHarbourColumn,
  isHarbourOwnerFilter,
  isHarbourPlanFilter,
} from 'src/constants/harbour'
import { mapHarbourSpartacusNotes } from './harbourNotes'

function toId(value) {
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'object' && value.toString) {
    const asString = String(value.toString()).trim()
    return asString && asString !== '[object Object]' ? asString : null
  }
  const asString = String(value).trim()
  return asString || null
}

function toIso(value) {
  if (!value) {
    return null
  }
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toISOString() : null
}

function toCount(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function toCountMap(value) {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }
  const mapped = {}
  for (const [key, entry] of Object.entries(value)) {
    mapped[String(key)] = toCount(entry)
  }
  return mapped
}

function toStringList(value, max) {
  const list = Array.isArray(value) ? value : []
  const cleaned = list.map(item => String(item || '').trim()).filter(Boolean)
  return max == null ? cleaned : cleaned.slice(0, max)
}

function mergeHarbourSisterTalkingPoint(
  points,
  sisterVenueCount,
  sisterGroup = null,
) {
  const sister =
    formatHarbourSisterGroupNudge(sisterGroup) ||
    formatHarbourSisterTalkingPoint(sisterVenueCount)
  const unique = []
  for (const point of points) {
    if (point && point !== sister && !unique.includes(point)) {
      unique.push(point)
    }
  }
  if (!sister) {
    return unique.slice(0, HARBOUR_MAX_TALKING_POINTS)
  }
  if (unique.length === 0) {
    return [sister]
  }
  return [unique[0], sister, ...unique.slice(1)].slice(
    0,
    HARBOUR_MAX_TALKING_POINTS,
  )
}

function mapSisterGroup(group) {
  if (!group || typeof group !== 'object') {
    return null
  }
  const siteCount = toCount(group.siteCount)
  const gapKey = String(group.gapKey || '').trim()
  if (siteCount < 2 || !HARBOUR_SISTER_GROUP_GAP_LABEL[gapKey]) {
    return null
  }
  const groupName = String(group.groupName || '').trim()
  const sites = (Array.isArray(group.sites) ? group.sites : [])
    .map(site => ({
      venueId: String(site?.venueId || '').trim(),
      venueName: String(site?.venueName || '').trim(),
    }))
    .filter(site => site.venueId)
  return {
    siteCount,
    gapKey,
    groupName: groupName || null,
    sites,
  }
}

export function getHarbourSisterGroupOtherSites(group, venueId) {
  const current = String(venueId || '').trim()
  return (Array.isArray(group?.sites) ? group.sites : []).filter(
    site => site.venueId && site.venueId !== current,
  )
}

export function getHarbourSisterGroupVenueIds(group, venueId) {
  const ids = []
  const seen = new Set()
  const push = value => {
    const id = String(value || '').trim()
    if (!id || seen.has(id)) {
      return
    }
    seen.add(id)
    ids.push(id)
  }
  push(venueId)
  for (const site of Array.isArray(group?.sites) ? group.sites : []) {
    push(site?.venueId)
  }
  return ids
}

export function formatHarbourDate(
  value,
  { empty = '—', format = 'D MMM YYYY' } = {},
) {
  if (!value) {
    return empty
  }
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format(format) : empty
}

export function formatHarbourFollowUpLabel(nextFollowUpAt, now = new Date()) {
  const iso = toIso(nextFollowUpAt)
  if (!iso) {
    return null
  }
  const due = dayjs(iso).startOf('day')
  const today = dayjs(now).startOf('day')
  const diff = due.diff(today, 'day')
  if (diff < 0) {
    const daysOverdue = Math.abs(diff)
    return daysOverdue === 1 ? '1d overdue' : `${daysOverdue}d overdue`
  }
  if (diff === 0) {
    return 'Today'
  }
  if (diff === 1) {
    return 'Tomorrow'
  }
  return formatHarbourDate(iso, { format: 'D MMM' })
}

function mapContact(contact = {}, fallbackPhone = '') {
  if (!contact || typeof contact !== 'object') {
    return { name: '', email: '', phone: fallbackPhone || '' }
  }
  return {
    name: String(contact.name || '').trim(),
    email: String(contact.email || '').trim(),
    phone: String(contact.phone || fallbackPhone || '').trim(),
  }
}

function mapLastUsedEntry(entry = {}) {
  const status = Object.values(HARBOUR_LAST_USED_STATUS).includes(entry.status)
    ? entry.status
    : HARBOUR_LAST_USED_STATUS.NEVER
  return {
    status,
    at: status === HARBOUR_LAST_USED_STATUS.DATE ? toIso(entry.at) : null,
    reason: entry.reason || null,
    ...(Number.isFinite(entry.count) ? { count: Number(entry.count) } : {}),
    ...(entry.expired ? { expired: true } : {}),
  }
}

export function mapHarbourLastUsed(lastUsed = {}) {
  const mapped = {}
  for (const feature of HARBOUR_LAST_USED_FEATURES) {
    mapped[feature] = {
      feature,
      label: getHarbourLastUsedFeatureLabel(feature),
      ...mapLastUsedEntry(lastUsed[feature] || {}),
    }
  }
  return mapped
}

function mapAdoptionTicks(ticks = []) {
  const rows = Array.isArray(ticks) ? ticks : []
  return rows.map(tick => ({
    key: tick.key || '',
    label: tick.label || '',
    status: tick.status || HARBOUR_LAST_USED_STATUS.NEVER,
    at: toIso(tick.at),
  }))
}

function mapAskSalesNudge(nudge) {
  if (!nudge || typeof nudge !== 'object') {
    return null
  }
  const label = String(nudge.label || '').trim()
  if (!label) {
    return null
  }
  return {
    label,
    upgradeScore:
      nudge.upgradeScore == null ? null : toCount(nudge.upgradeScore),
  }
}

function mapAskProductNudge(nudge) {
  if (!nudge || typeof nudge !== 'object') {
    return null
  }
  const label = String(nudge.label || '').trim()
  if (!label) {
    return null
  }
  return {
    label,
    csTouchCount:
      nudge.csTouchCount == null ? null : toCount(nudge.csTouchCount),
  }
}

function mapRisk(risk) {
  if (!risk || typeof risk !== 'object') {
    return null
  }
  const drivers = Array.isArray(risk.riskScoreDrivers)
    ? risk.riskScoreDrivers
    : []
  return {
    riskScore: risk.riskScore == null ? null : Number(risk.riskScore),
    riskScoreDrivers: drivers.map(driver => ({
      label: driver?.label || '',
      points: driver?.points == null ? null : Number(driver.points),
    })),
  }
}

/**
 * One venue card. Backend already chose why-here; we only format for the UI.
 */
export function mapHarbourBoardCard(row = {}, { now = new Date() } = {}) {
  const venueId = toId(row.venueId)
  const column = isHarbourColumn(row.column)
    ? row.column
    : HARBOUR_DEFAULT_COLUMN
  const nextFollowUpAt = toIso(row.nextFollowUpAt)
  const lastCsTouchedAt = toIso(row.lastCsTouchedAt)
  const inboundSource = row.inboundSource || null
  const lastOutcome = row.lastOutcome || null
  const lastIntent = row.lastIntent || null
  const lifecycle = row.lifecycle || null
  const phoneNumber = String(row.phoneNumber || '').trim()

  return {
    key: venueId,
    venueId,
    venueName: String(row.venueName || '').trim(),
    plan: String(row.plan || 'Free').trim() || 'Free',
    paid: Boolean(row.paid),
    phoneNumber,
    contact: mapContact(row.contact, phoneNumber),
    sisterVenueCount: toCount(row.sisterVenueCount),
    sisterGroup: mapSisterGroup(row.sisterGroup),
    column,
    columnLabel: getHarbourColumnLabel(column),
    ownerAccountId: toId(row.ownerAccountId),
    ownerName: String(row.ownerName || '').trim(),
    nextFollowUpAt,
    nextFollowUpLabel: formatHarbourFollowUpLabel(nextFollowUpAt, now),
    snoozeUntil: toIso(row.snoozeUntil),
    lastCsTouchedAt,
    lastTouchedLabel: lastCsTouchedAt
      ? formatHarbourDate(lastCsTouchedAt, { format: 'D MMM' })
      : '',
    lastOutcome,
    lastOutcomeLabel: lastOutcome ? getHarbourOutcomeLabel(lastOutcome) : '',
    lastIntent,
    lastIntentLabel: lastIntent ? getHarbourIntentLabel(lastIntent) : '',
    inboundSource,
    inboundSourceLabel: inboundSource
      ? getHarbourInboundSourceLabel(inboundSource)
      : '',
    lastActivitySummary: String(row.lastActivitySummary || '').trim(),
    overdue: Boolean(row.overdue),
    forgotten: Boolean(row.forgotten),
    whyHere: String(row.whyHere || '').trim(),
    suggestedNextStep: String(row.suggestedNextStep || '').trim(),
    talkingPoints: mergeHarbourSisterTalkingPoint(
      toStringList(row.talkingPoints),
      toCount(row.sisterVenueCount),
      mapSisterGroup(row.sisterGroup),
    ),
    whatsAppOpener: String(row.whatsAppOpener || row.whatsAppText || '').trim(),
    adoptionTicks: mapAdoptionTicks(row.adoptionTicks),
    lastUsed: mapHarbourLastUsed(row.lastUsed),
    lifecycle,
    lifecycleLabel: lifecycle ? getHarbourLifecycleLabel(lifecycle) : '',
    askSalesNudge: mapAskSalesNudge(row.askSalesNudge),
    askProductNudge: mapAskProductNudge(row.askProductNudge),
    upgradeScore: row.upgradeScore == null ? null : Number(row.upgradeScore),
    risk: mapRisk(row.risk),
    lastLoginAt: toIso(row.lastUsed?.[HARBOUR_LAST_USED_FEATURE.LOGIN]?.at),
  }
}

export function mapHarbourPulse(pulse = {}) {
  const raw =
    pulse && typeof pulse === 'object' && !Array.isArray(pulse) ? pulse : {}
  const mapped = {
    paidAtRisk: toCount(raw.paidAtRisk),
    freeSlipping: toCount(raw.freeSlipping),
    overdueFollowUps: toCount(raw.overdueFollowUps),
    unclaimedNeedsYou: toCount(raw.unclaimedNeedsYou),
    snoozedThisWeek: toCount(raw.snoozedThisWeek),
    recoveredThisWeek: toCount(raw.recoveredThisWeek),
    forgotten: toCount(raw.forgotten),
    needsYouCap: toCount(raw.needsYouCap) || HARBOUR_NEEDS_YOU_CAP,
    overdueFollowUpsByOwner: toCountMap(raw.overdueFollowUpsByOwner),
    forgottenByOwner: toCountMap(raw.forgottenByOwner),
  }
  for (const key of HARBOUR_PULSE_FORBIDDEN_KEYS) {
    if (Object.prototype.hasOwnProperty.call(mapped, key)) {
      delete mapped[key]
    }
  }
  return mapped
}

function mapCardList(rows, options) {
  return (Array.isArray(rows) ? rows : [])
    .map(row => mapHarbourBoardCard(row, options))
    .filter(card => card.venueId)
}

function capHarbourThrivingCards(cards = []) {
  return (Array.isArray(cards) ? cards : []).slice(0, HARBOUR_NEEDS_YOU_CAP)
}

function capHarbourAskSalesCards(cards = []) {
  return (Array.isArray(cards) ? cards : []).slice(0, HARBOUR_NEEDS_YOU_CAP)
}

function mapBoardColumns(columns = {}, options) {
  const mapped = {}
  for (const column of HARBOUR_BOARD_VISIBLE_COLUMNS) {
    mapped[column] = mapCardList(columns[column], options)
  }
  if (Array.isArray(columns[HARBOUR_COLUMN.PARKED])) {
    mapped[HARBOUR_COLUMN.PARKED] = mapCardList(
      columns[HARBOUR_COLUMN.PARKED],
      options,
    )
  }
  return mapped
}

export function createEmptyHarbourColumns() {
  const mapped = {}
  for (const column of HARBOUR_BOARD_VISIBLE_COLUMNS) {
    mapped[column] = []
  }
  return mapped
}

export function createEmptyHarbourBoard(section = HARBOUR_BOARD_SECTION.ALL) {
  return {
    today: [],
    columns: createEmptyHarbourColumns(),
    needsYou: {
      shown: 0,
      total: 0,
      loginGap: 0,
      adoptionGap: 0,
      other: 0,
      caption: null,
      detail: null,
      cap: HARBOUR_NEEDS_YOU_CAP,
    },
    thriving: {
      enabled: false,
      available: false,
      shown: 0,
      total: 0,
      cap: HARBOUR_NEEDS_YOU_CAP,
      cards: [],
    },
    askSales: {
      shown: 0,
      total: 0,
      cap: HARBOUR_NEEDS_YOU_CAP,
      cards: [],
    },
    hydrated: 0,
    filters: {
      ownerFilter: HARBOUR_DEFAULT_OWNER_FILTER,
      planFilter: HARBOUR_DEFAULT_PLAN_FILTER,
      hideParked: HARBOUR_DEFAULT_HIDE_PARKED,
      hasPhone: HARBOUR_DEFAULT_HAS_PHONE,
      section,
    },
  }
}

export function mapHarbourBoard(board = {}, { now = new Date() } = {}) {
  const options = { now }
  const columns = mapBoardColumns(board.columns, options)
  if (columns[HARBOUR_COLUMN.NEEDS_YOU].length > HARBOUR_NEEDS_YOU_CAP) {
    columns[HARBOUR_COLUMN.NEEDS_YOU] = columns[HARBOUR_COLUMN.NEEDS_YOU].slice(
      0,
      HARBOUR_NEEDS_YOU_CAP,
    )
  }

  const shown = Math.min(
    board.needsYou?.shown == null
      ? columns[HARBOUR_COLUMN.NEEDS_YOU].length
      : toCount(board.needsYou.shown),
    HARBOUR_NEEDS_YOU_CAP,
  )
  const total = toCount(board.needsYou?.total)
  const loginGap = toCount(board.needsYou?.loginGap)
  const adoptionGap = toCount(board.needsYou?.adoptionGap)
  const other = toCount(board.needsYou?.other)
  const iceberg = shouldShowHarbourNeedsYouIceberg({ shown, total })
  const caption =
    board.needsYou?.caption || formatHarbourNeedsYouCaption({ shown, total })
  const detail = iceberg
    ? board.needsYou?.detail ||
      formatHarbourNeedsYouCaptionDetail({ loginGap, adoptionGap, other })
    : null
  const filters = board.filters || {}
  const thrivingCards = capHarbourThrivingCards(
    mapCardList(board.thriving?.cards, options),
  )
  const thrivingTotal = toCount(board.thriving?.total)
  const thrivingShown = Math.min(
    board.thriving?.shown == null
      ? thrivingCards.length
      : toCount(board.thriving.shown),
    HARBOUR_NEEDS_YOU_CAP,
  )
  const askSalesCards = capHarbourAskSalesCards(
    mapCardList(board.askSales?.cards, options),
  )
  const askSalesTotal = toCount(board.askSales?.total)
  const askSalesShown = Math.min(
    board.askSales?.shown == null
      ? askSalesCards.length
      : toCount(board.askSales.shown),
    HARBOUR_NEEDS_YOU_CAP,
  )

  return {
    today: mapCardList(board.today, options),
    columns,
    needsYou: {
      shown: Math.min(shown, HARBOUR_NEEDS_YOU_CAP),
      total,
      loginGap,
      adoptionGap,
      other,
      caption,
      detail,
      cap: toCount(board.needsYou?.cap) || HARBOUR_NEEDS_YOU_CAP,
    },
    thriving: {
      enabled: Boolean(board.thriving?.enabled),
      available: board.thriving?.available !== false && thrivingTotal > 0,
      shown: thrivingShown,
      total: thrivingTotal,
      cap: toCount(board.thriving?.cap) || HARBOUR_NEEDS_YOU_CAP,
      cards: thrivingCards,
    },
    askSales: {
      shown: askSalesShown,
      total: askSalesTotal,
      cap: toCount(board.askSales?.cap) || HARBOUR_NEEDS_YOU_CAP,
      cards: askSalesCards,
    },
    hydrated: Math.min(toCount(board.hydrated), HARBOUR_BOARD_PAYLOAD_MAX),
    filters: {
      ownerFilter: isHarbourOwnerFilter(filters.ownerFilter)
        ? filters.ownerFilter
        : HARBOUR_DEFAULT_OWNER_FILTER,
      planFilter: isHarbourPlanFilter(filters.planFilter)
        ? filters.planFilter
        : HARBOUR_DEFAULT_PLAN_FILTER,
      hideParked:
        filters.hideParked == null
          ? HARBOUR_DEFAULT_HIDE_PARKED
          : Boolean(filters.hideParked),
      hasPhone:
        filters.hasPhone == null
          ? HARBOUR_DEFAULT_HAS_PHONE
          : Boolean(filters.hasPhone),
      section: filters.section || HARBOUR_BOARD_SECTION.ALL,
    },
  }
}

export function mapHarbourSearchHit(row = {}) {
  return {
    venueId: toId(row.venueId || row._id),
    venueName: String(row.venueName || row.title || '').trim(),
    phoneNumber: String(row.phoneNumber || '').trim(),
  }
}

export function mapHarbourSearch(result = {}) {
  const venues = (Array.isArray(result.venues) ? result.venues : [])
    .map(mapHarbourSearchHit)
    .filter(hit => hit.venueId)
    .slice(0, HARBOUR_SEARCH_LIMIT)

  return {
    search: String(result.search || '').trim(),
    gap: isHarbourAdoptionGap(result.gap) ? result.gap : '',
    limit: HARBOUR_SEARCH_LIMIT,
    venues,
  }
}

function mapActivity(entries = []) {
  return (Array.isArray(entries) ? entries : []).map(entry => ({
    activityId: toId(entry.activityId || entry._id),
    at: toIso(entry.at),
    accountId: toId(entry.accountId),
    name: String(entry.name || '').trim(),
    email: String(entry.email || '').trim(),
    type: entry.type || '',
    summary: String(entry.summary || '').trim(),
    metadata:
      entry.metadata && typeof entry.metadata === 'object'
        ? entry.metadata
        : {},
  }))
}

function mapLinkedDeals(deals = []) {
  return (Array.isArray(deals) ? deals : [])
    .map(deal => ({
      dealId: toId(deal.dealId || deal._id),
      name: String(deal.name || '').trim(),
      stage: deal.stage || null,
      readiness: deal.readiness ?? null,
      readinessNote: String(deal.readinessNote || '').trim(),
      ownerAccountId: toId(deal.ownerAccountId),
      lastActivitySummary: String(deal.lastActivitySummary || '').trim(),
      nextFollowUpAt: toIso(deal.nextFollowUpAt),
    }))
    .filter(deal => deal.dealId)
    .slice(0, HARBOUR_DRAWER_LINKED_DEALS_LIMIT)
}

function mapUsers(users = []) {
  return (Array.isArray(users) ? users : []).map(user => ({
    accountId: toId(user.accountId || user._id),
    name: String(user.name || '').trim(),
    email: String(user.email || '').trim(),
    isOwner: Boolean(user.isOwner),
    lastSeenAt: toIso(user.lastSeenAt),
  }))
}

function mapStartWork(startWork = {}) {
  return {
    canStartWork: Boolean(startWork.canStartWork),
    alreadyOwned: Boolean(startWork.alreadyOwned),
    alreadyTaken: Boolean(startWork.alreadyTaken),
    takenMessage: startWork.takenMessage || null,
  }
}

export function mapHarbourCase(row = {}) {
  if (!row || typeof row !== 'object') {
    return null
  }
  const venueId = toId(row.venueId)
  if (!venueId && !toId(row._id)) {
    return null
  }
  return {
    caseId: toId(row._id || row.caseId),
    venueId,
    column: isHarbourColumn(row.column) ? row.column : HARBOUR_DEFAULT_COLUMN,
    ownerAccountId: toId(row.ownerAccountId),
    inboundSource: row.inboundSource || null,
    nextFollowUpAt: toIso(row.nextFollowUpAt),
    snoozeUntil: toIso(row.snoozeUntil),
    lastOutcome: row.lastOutcome || null,
    lastIntent: row.lastIntent || null,
    lastActivitySummary: String(row.lastActivitySummary || '').trim(),
    lastCsTouchedAt: toIso(row.lastCsTouchedAt),
    parkReason: row.parkReason || null,
  }
}

export function mapHarbourDrawer(drawer = {}, { now = new Date() } = {}) {
  const card = mapHarbourBoardCard(drawer, { now })
  const notes = mapHarbourSpartacusNotes(drawer.notes)

  return {
    ...card,
    lastNote: String(drawer.lastNote || '').trim(),
    users: mapUsers(drawer.users),
    singleUserRisk: {
      userCount: toCount(drawer.singleUserRisk?.userCount),
      caption: drawer.singleUserRisk?.caption || null,
      lastSeenAt: toIso(drawer.singleUserRisk?.lastSeenAt),
    },
    whatsAppText: String(
      drawer.whatsAppText || card.whatsAppOpener || '',
    ).trim(),
    notes: {
      ...notes,
      venueId: toId(notes.venueId) || card.venueId,
    },
    linkedDeals: mapLinkedDeals(drawer.linkedDeals),
    activity: mapActivity(drawer.activity),
    case: drawer.case ? mapHarbourCase(drawer.case) : null,
    startWork: mapStartWork(drawer.startWork),
  }
}

function stripForbiddenQueryKeys(params = {}) {
  const cleaned = { ...params }
  for (const key of HARBOUR_BOARD_FORBIDDEN_QUERY_KEYS) {
    delete cleaned[key]
  }
  delete cleaned.page
  delete cleaned.pageSize
  delete cleaned.limit
  delete cleaned.skip
  return cleaned
}

export function buildHarbourPulseQueryParams({
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
} = {}) {
  const params = {}
  if (isHarbourPlanFilter(planFilter)) {
    params.plan = planFilter
  }
  return stripForbiddenQueryKeys(params)
}

export function buildHarbourBoardQueryParams({
  ownerFilter = HARBOUR_DEFAULT_OWNER_FILTER,
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
  hideParked = HARBOUR_DEFAULT_HIDE_PARKED,
  hasPhone = HARBOUR_DEFAULT_HAS_PHONE,
  thriving = HARBOUR_DEFAULT_THRIVING,
} = {}) {
  const params = {}
  if (isHarbourOwnerFilter(ownerFilter)) {
    params.owner = ownerFilter
  }
  if (isHarbourPlanFilter(planFilter)) {
    params.plan = planFilter
  }
  params.hideParked = Boolean(hideParked)
  params.hasPhone = Boolean(hasPhone)
  if (thriving) {
    params.thriving = true
  }
  return stripForbiddenQueryKeys(params)
}

export function mapHarbourOllieSuggestion(data = {}) {
  return {
    nextStep: String(data.nextStep || '').trim(),
    whatsAppText: String(data.whatsAppText || '').trim(),
    emailText: String(data.emailText || '').trim(),
    logNote: String(data.logNote || '').trim(),
    copyable: data.copyable !== false,
    send: false,
  }
}

export function mapHarbourMutationResult(data = {}) {
  const mapped = { ...data }
  if (data.case) {
    mapped.case = mapHarbourCase(data.case)
  }
  if (data.venueId) {
    mapped.venueId = toId(data.venueId)
  }
  if (Array.isArray(data.venueIds)) {
    mapped.venueIds = data.venueIds.map(toId).filter(Boolean)
  }
  if (Array.isArray(data.skipped)) {
    mapped.skipped = data.skipped
  }
  if (data.kind) {
    mapped.kind = data.kind
  }
  if (data.snoozeUntil) {
    mapped.snoozeUntil = toIso(data.snoozeUntil)
  }
  if (data.nextFollowUpAt) {
    mapped.nextFollowUpAt = toIso(data.nextFollowUpAt)
  }
  if (data.lastNote != null) {
    mapped.lastNote = String(data.lastNote || '').trim()
  } else if (data.note?.noteText) {
    mapped.lastNote = String(data.note.noteText).trim()
  }
  if (data.outcome) {
    mapped.outcome = data.outcome
  }
  return mapped
}
